import React from "react";
import { COREMenuItem } from "../../../../COREDesignSystem/Navigation/COREMenuItem";
import { CORESubMenu } from "../../../../COREDesignSystem/Navigation/CORESubMenu";
import { OHLCPriceHistoryByProductType } from "../../../projectMarketplaceLegacy/widgets/OHLCPriceHistoryByProductType";
import { SetupOHLCPriceHistoryWidget } from "../../../projectMarketplaceLegacy/widgets/SetupOHLCPriceHistoryWidget";
import {
  currentWidgetFilters,
  onSelectSetupMenu,
  WidgetPanel,
  WidgetProps,
} from "../../WidgetPanel";
import { CORELoading } from "../../../../COREDesignSystem/Feedback/CORELoading";
import { COREEmpty } from "../../../../COREDesignSystem/Content/COREEmpty";
import { PriceHistoryChartFilterValues } from "../../../projectMarketplaceLegacy/PriceHistoryChartWithFilter";
import {
  FeatureFlag,
  useFeatureFlags,
} from "../../../../shared/customHoooks/useFeatureFlags";
import { DropdownMenuItem } from "../../../../COREDesignSystem/Form/COREDropdownMenu";
import { COREDivider } from "../../../../COREDesignSystem/Layout/COREDivider";
import { COREMenuItemGroup } from "../../../../COREDesignSystem/Navigation/COREMenuItemGroup";
import { generateTestId } from "../../../../shared/testids/testids";
import { getProductTypeFromScopes } from "../../../../shared/customHooks/useProductTypeByScope";
import { ProductType } from "../../../../openapi-typescript/common/product_types";

type OHLCPriceHistoryWidgetType<T, WidgetFilterType extends object> = {
  props: WidgetProps<T, WidgetFilterType>;
};

export type OHLCProductType =
  | "accu"
  | "nzu"
  | "cer"
  | "gs"
  | "vcu"
  | "lgc"
  | "stc"
  | "esc"
  | "veec"
  | "prc"
  | "geo"
  | "ngeo"
  | "cgeo"
  | "solar"
  | "inversesolar"
  | "superpeak"
  | "flat"
  | "peak"
  | "offpeak"
  | "cap300"
  | "acr"
  | "car";
export type OHLCPriceHistoryWidgetFilters = {};

export const carbonIntAllProducts = ["accu", "smc", "nzu"];
export const environmentalAllProducts = ["esc", "lgc", "prc", "stc", "veec"];
export const powerAllProducts = [
  "flat",
  "peak",
  "off peak",
  "cap300",
  "super peak",
  "inverse solar",
  "solar",
];

export const isEnableOHLCPriceHistoryWidget = (features?: FeatureFlag[]) => {
  if (!features) return false;
  return features.includes("markets");
};

const getMenuPermission = (
  selectedType: OHLCProductType | undefined,
  features: FeatureFlag[]
): OHLCProductType | "havePermission" | "noPermission" => {
  const product = selectedType
    ?.toLocaleLowerCase()
    .replace(/\s/g, "")
    .replace("$", "") as OHLCProductType;
  if (
    !isEnableOHLCPriceHistoryWidget(features) ||
    (selectedType && !features.includes(`core.product.${product}.read`))
  ) {
    return "noPermission";
  } else {
    return "havePermission";
  }
};

const getProductInScope = (
  permissionProduct: ProductType["code"][],
  checkProduct: string[]
) => {
  return permissionProduct
    .filter((product) => {
      return checkProduct.includes(product);
    })
    .sort((item, product) => {
      const indexItem = checkProduct.indexOf(item);
      const indexProduct = checkProduct.indexOf(product);
      const safeIndexItem = indexItem === -1 ? Infinity : indexItem;
      const safeIndexProduct = indexProduct === -1 ? Infinity : indexProduct;
      return safeIndexItem - safeIndexProduct;
    });
};

export const OHLCPriceHistoryWidget = ({
  props: widgetProps,
}: OHLCPriceHistoryWidgetType<
  OHLCProductType,
  PriceHistoryChartFilterValues
>) => {
  const { sync, loading, features } = useFeatureFlags();
  if (loading && !sync) {
    return <CORELoading />;
  }

  const currentProps = currentWidgetFilters(widgetProps);
  const permissionType = getMenuPermission(currentProps?.widgetType, features);
  const productScope = getProductTypeFromScopes(features);
  const carbonProducts = getProductInScope(productScope, carbonIntAllProducts);
  const envProducts = getProductInScope(productScope, environmentalAllProducts);
  const powerProducts = getProductInScope(productScope, powerAllProducts);

  if (
    permissionType === "noPermission" ||
    (carbonProducts.length <= 0 &&
      envProducts.length <= 0 &&
      powerProducts.length <= 0)
  )
    return (
      <COREEmpty
        testID="dashboard-ohlc-price-history-widgets-empty"
        description="No content available."
      />
    );

  const currentWidgetType =
    permissionType === "havePermission"
      ? currentProps?.widgetType
      : permissionType ?? currentProps?.widgetType;

  const productSelectMenu: DropdownMenuItem[] = [
    ...(carbonProducts.length > 0
      ? [
          {
            closeWhenClick: true,
            component: (
              <COREMenuItemGroup
                key="carbon-item"
                testID="dashboard-carbon-menu-group-item"
                title="Carbon market"
              >
                <COREDivider space="none" />
                {carbonProducts.map((product) => (
                  <COREMenuItem
                    testID={generateTestId("dashboard", "add-carbon-item")}
                    key={`${product}-item`}
                    onClick={() => {
                      onSelectSetupMenu(widgetProps, product);
                    }}
                  >
                    {product.toUpperCase()}
                  </COREMenuItem>
                ))}
              </COREMenuItemGroup>
            ),
          },
        ]
      : []),
    ...(envProducts.length > 0
      ? [
          {
            closeWhenClick: true,
            component: (
              <COREMenuItemGroup
                key="env-item"
                testID="dashboard-env-menu-group-item"
                title="Environmental market"
              >
                <COREDivider space="none" />
                {envProducts.map((product) => (
                  <COREMenuItem
                    testID={generateTestId("dashboard", "add-env-item")}
                    key={`${product}-item`}
                    onClick={() => {
                      onSelectSetupMenu(widgetProps, product);
                    }}
                  >
                    {product.toUpperCase()}
                  </COREMenuItem>
                ))}
              </COREMenuItemGroup>
            ),
          },
        ]
      : []),
    ...(powerProducts.length > 0
      ? [
          {
            closeWhenClick: true,
            component: (
              <COREMenuItemGroup
                key="power-item"
                testID="dashboard-power-menu-group-item"
                title="Power markets"
              >
                <COREDivider space="none" />
                {powerProducts.map((product) => (
                  <COREMenuItem
                    testID={generateTestId("dashboard", "add-power-item")}
                    key={`${product}-item`}
                    onClick={() => {
                      onSelectSetupMenu(widgetProps, product);
                    }}
                  >
                    {product === "cap300"
                      ? "$300 Cap"
                      : product.charAt(0).toUpperCase() + product.slice(1)}
                  </COREMenuItem>
                ))}
              </COREMenuItemGroup>
            ),
          },
        ]
      : []),
  ];

  const subMenu = productSelectMenu.map((menu) => menu.component);
  const interactionsMenu = [
    {
      component: (
        <CORESubMenu testID="storybook-CORESubMenu" title={"Widget setup"}>
          <div style={{ height: "380px", overflowY: "auto" }}>{subMenu}</div>
        </CORESubMenu>
      ),
      closeWhenClick: true,
    },
  ];

  const widgetTitle = currentWidgetType
    ? currentWidgetType === "cap300"
      ? "$300 CAP - Price history"
      : `${currentWidgetType.toUpperCase()} - Price history`
    : "Price history - widget setup";

  return (
    <WidgetPanel
      interactionsMenu={interactionsMenu}
      testID={"dashboard-ohlc-price-history-widgets"}
      panelTitle={widgetTitle}
    >
      {currentWidgetType ? (
        <OHLCPriceHistoryByProductType
          testID={"dashboard-ohlc-price-history-widgets-content"}
          widgetProps={widgetProps}
          widgetType={currentWidgetType}
        />
      ) : (
        <SetupOHLCPriceHistoryWidget
          testID={"dashboard-ohlc-price-history-widgets-setup"}
          widgetProps={widgetProps}
          carbonProducts={
            carbonProducts.length > 0 ? carbonProducts : undefined
          }
          envProducts={envProducts.length > 0 ? envProducts : undefined}
          powerProducts={powerProducts.length > 0 ? powerProducts : undefined}
        />
      )}
    </WidgetPanel>
  );
};
